import { create } from "zustand";

/**
 * Per-thread-item store that provides O(1) lookup by remoteId.
 * Replaces the O(n) `threadItems.find(...)` + full-array context subscription
 * in ThreadListItem. Each item subscribes only to its own slice.
 */

export type ThreadListItemData = {
  isPinned: boolean;
  isStreaming: boolean;
};

type ThreadListStoreState = {
  items: Record<string, ThreadListItemData>;
  /** Batch-update items from the thread list fetch. */
  syncItems: (entries: Array<{ remoteId: string; isPinned: boolean }>) => void;
  /** Update streaming status for a set of thread IDs. */
  syncStreaming: (activeIds: Set<string>) => void;
};

export const useThreadListStore = create<ThreadListStoreState>((set, get) => ({
  items: {},
  syncItems: (entries) => {
    const next: Record<string, ThreadListItemData> = {};
    for (const e of entries) {
      const existing = get().items[e.remoteId];
      next[e.remoteId] = {
        isPinned: e.isPinned,
        isStreaming: existing?.isStreaming ?? false,
      };
    }
    set({ items: next });
  },
  syncStreaming: (activeIds) => {
    const current = get().items;
    let changed = false;
    const next = { ...current };

    // Update existing items
    for (const [id, data] of Object.entries(next)) {
      const shouldStream = activeIds.has(id);
      if (data.isStreaming !== shouldStream) {
        next[id] = { ...data, isStreaming: shouldStream };
        changed = true;
      }
    }

    // Create temporary entries for IDs not yet materialized by syncItems.
    // syncItems preserves existing isStreaming, so this flag survives the race.
    for (const id of activeIds) {
      if (!next[id]) {
        next[id] = { isPinned: false, isStreaming: true };
        changed = true;
      }
    }

    if (changed) set({ items: next });
  },
}));
